import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import * as XLSX from "xlsx";
import { Ic, DomainTag, StatusBadge, Modal, AutocompleteTextInput, inputStyle, btnPrimary, btnSecondary, btnGhost, showStyledAlert, showStyledConfirm } from "../components/UI";
import { addVolunteer, updateVolunteer, deleteVolunteer } from "../utils/firestore";
import { getPrimaryCoordinatorId, getPrimaryTeamLeadId, getVolunteerCoordinatorIds, getVolunteerTeamLeadIds } from "../utils/volunteerAssignments";
import { formatContributionCurrency, getActivityContributionTotal, getActivityContributionValueTotal, sumActivitiesContributionTotal, sumActivitiesContributionValueTotal } from "../utils/activityContributions";

/* ─── Inline Icons ───────────────────────────────────────────── */
function WhatsAppIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.47 14.38c-.27-.14-1.58-.78-1.83-.86-.24-.09-.41-.14-.58.14-.17.27-.67.86-.82 1.03-.15.17-.3.2-.56.07-.27-.14-1.12-.41-2.13-1.3-.79-.7-1.32-1.57-1.47-1.84-.15-.27-.02-.41.11-.55.12-.12.27-.3.41-.45.14-.15.18-.27.27-.45.09-.17.05-.33-.02-.47-.07-.14-.58-1.4-.8-1.92-.21-.5-.43-.43-.58-.44h-.5c-.17 0-.45.07-.68.33-.23.27-.88.86-.88 2.1 0 1.24.9 2.44 1.02 2.61.12.17 1.77 2.7 4.29 3.79 2.52 1.09 2.52.73 2.98.69.45-.04 1.46-.6 1.67-1.17.21-.57.21-1.06.15-1.17-.06-.11-.23-.17-.5-.31Z" />
      <path d="M20.52 3.48A11.87 11.87 0 0 0 12.06 0C5.53 0 .2 5.32.2 11.86c0 2.09.55 4.14 1.6 5.95L0 24l6.36-1.67a11.8 11.8 0 0 0 5.7 1.46h.01c6.54 0 11.87-5.33 11.87-11.87 0-3.17-1.23-6.14-3.42-8.34Zm-8.45 18.31h-.01a9.88 9.88 0 0 1-5.04-1.38l-.36-.22-3.77.99 1.01-3.67-.24-.38a9.86 9.86 0 0 1-1.51-5.27c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.11 1.03 6.97 2.89a9.8 9.8 0 0 1 2.9 6.99c0 5.46-4.44 9.9-9.86 9.9Z" />
    </svg>
  );
}

function UploadIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function InfoIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
    </svg>
  );
}

function UserPlusIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}

function ChevronRightIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function XIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function StarIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function CalendarIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function ActivityIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function MapPinIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function MailIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function ClockIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function NoteIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function UsersIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function RepeatIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}

function getContactValue(entry) {
  if (Array.isArray(entry)) return `${entry[0] || ""}`.trim();
  return `${entry || ""}`.trim();
}

/* ─── Engagement Type Badge ──────────────────────────────────── */
function EngagementBadge({ type }) {
  const isFixed = type === "fixed";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, fontFamily: "Heebo",
      background: isFixed ? "#EFF6FF" : "#FFF7ED",
      color: isFixed ? "#2563EB" : "#C2410C",
      border: `1px solid ${isFixed ? "#DBEAFE" : "#FED7AA"}`,
    }}>
      {isFixed ? "קבוע" : "מזדמן"}
    </span>
  );
}

/* ─── Shared Styles ──────────────────────────────────────────── */
const ACCENT = "#C41E3A";
const ACCENT_LIGHT = "#FFF1F3";
const ACCENT_HOVER = "#A3182F";
const BORDER = "#E5E7EB";
const BORDER_LIGHT = "#F3F4F6";
const TEXT_PRIMARY = "#1F2937";
const TEXT_SECONDARY = "#6B7280";
const TEXT_MUTED = "#9CA3AF";
const BG_SUBTLE = "#F9FAFB";

const iconBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 36, height: 36, borderRadius: 10, border: `1px solid ${BORDER}`,
  background: "white", cursor: "pointer", color: TEXT_SECONDARY, transition: "all 0.15s",
};
const iconBtnAccent = {
  ...iconBtn,
  background: ACCENT, border: `1px solid ${ACCENT}`, color: "white",
};
const iconBtnWithLabel = {
  ...iconBtn,
  width: "auto", padding: "0 14px", gap: 6, fontSize: 13, fontWeight: 500, fontFamily: "Heebo",
};

/* ─── Slide-out Drawer ───────────────────────────────────────── */
function VolunteerDrawer({ volunteer, onClose, onView, onEdit, onDelete, domains, coordinators, volunteers = [], activities, isMobile }) {
  const [closing, setClosing] = useState(false);
  const monthAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d; }, []);
  const domainById = useMemo(() => { const m = {}; domains.forEach(d => m[d.id] = d); return m; }, [domains]);

  const fullName = (v) => {
    const first = (v?.firstName || "").trim();
    const last = (v?.lastName || "").trim();
    if (first || last) return `${first} ${last}`.trim();
    return (v?.name || "").trim();
  };

  const monthHours = activities.filter(a => a.volunteerId === volunteer.id && new Date(a.date) >= monthAgo).reduce((s, a) => s + Number(a.duration), 0);
  const totalActivities = activities.filter(a => a.volunteerId === volunteer.id).length;
  const totalHours = activities.filter(a => a.volunteerId === volunteer.id).reduce((s, a) => s + Number(a.duration), 0);
  const totalContributions = sumActivitiesContributionTotal(activities.filter(a => a.volunteerId === volunteer.id));
  const totalContributionValue = sumActivitiesContributionValueTotal(activities.filter(a => a.volunteerId === volunteer.id));
  const rating = Math.round((volunteer.points || 0) + (totalHours * 2) + (totalActivities * 5));

  const normalizePhone = (phone) => `${phone || ""}`.replace(/[^\d+]/g, "");
  const toWhatsappNumber = (phone) => {
    const digits = `${phone || ""}`.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("972")) return digits;
    if (digits.startsWith("0")) return `972${digits.slice(1)}`;
    return digits;
  };
  const telHref = normalizePhone(volunteer.phone) ? `tel:${normalizePhone(volunteer.phone)}` : "";
  const waHref = toWhatsappNumber(volunteer.phone) ? `https://wa.me/${toWhatsappNumber(volunteer.phone)}` : "";
  const coordinatorNames = getVolunteerCoordinatorIds(volunteer)
    .map((id) => coordinators.find((c) => c.id === id)?.name || coordinators.find((c) => c.id === id)?.email || id)
    .filter(Boolean);
  const teamLeadNames = getVolunteerTeamLeadIds(volunteer)
    .map((id) => volunteers.find((v) => v.id === id)?.name || id)
    .filter(Boolean);
  const volunteerModeLabel = (volunteer?.engagementType || "fixed") === "fixed" ? "קבוע" : "מזדמן";

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose(), 260);
  }, [onClose]);

  const drawerWidth = isMobile ? "100vw" : 420;

  const InfoRow = ({ icon, label, value, children }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
      <div style={{ color: TEXT_MUTED, flexShrink: 0, marginTop: 2 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_MUTED, marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
        {children || <div style={{ fontSize: 14, color: TEXT_PRIMARY, wordBreak: "break-word" }}>{value || "—"}</div>}
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9998,
          opacity: closing ? 0 : 1, transition: "opacity 0.25s ease",
          backdropFilter: "blur(2px)",
        }}
      />
      {/* Drawer */}
      <div
        dir="rtl"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: drawerWidth, maxWidth: "100vw",
          background: "white", zIndex: 9999,
          boxShadow: "-8px 0 30px rgba(0,0,0,0.12)",
          transform: closing ? "translateX(100%)" : "translateX(0)",
          transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex", flexDirection: "column",
          animation: "drawerSlideIn 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <style>{`
          @keyframes drawerSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        `}</style>

        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${BORDER}`, background: BG_SUBTLE }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <button onClick={handleClose} style={{ ...iconBtn, width: 32, height: 32 }}><XIcon size={16} /></button>
            <div style={{ display: "flex", gap: 6 }}>
              <button style={iconBtn} onClick={() => onEdit(volunteer)} title="עריכה"><Ic.Edit /></button>
              <button style={{ ...iconBtn, color: "#EF4444" }} onClick={() => onDelete(volunteer.id)} title="מחיקה"><Ic.Trash /></button>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 14, flexShrink: 0,
              background: `linear-gradient(135deg, ${ACCENT}, #E8364F)`,
              color: "white", display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, fontWeight: 700, fontFamily: "Assistant",
              boxShadow: "0 4px 12px rgba(196,30,58,0.3)",
            }}>
              {fullName(volunteer)?.[0] || "?"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "Assistant", color: TEXT_PRIMARY, marginBottom: 2 }}>
                {fullName(volunteer)}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <StatusBadge status={volunteer.status} />
                <EngagementBadge type={volunteer.engagementType || "fixed"} />
                {volunteer.isTeamLead === true && <span style={{ fontSize: 11, fontWeight: 700, color: "#065F46", background: "#ECFDF5", border: "1px solid #BBF7D0", borderRadius: 999, padding: "2px 8px" }}>ראש צוות</span>}
                {volunteer.city && <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>{volunteer.city}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", borderBottom: `1px solid ${BORDER}` }}>
          {[
            { label: "דירוג", value: rating, icon: <StarIcon size={13} />, color: "#F59E0B" },
            { label: "שעות חודשי", value: monthHours.toFixed(1), icon: <ClockIcon size={13} />, color: "#3B82F6" },
            { label: "תרומות", value: totalContributions, icon: <ActivityIcon size={13} />, color: "#C2410C" },
            { label: "שווי תרומות", value: formatContributionCurrency(totalContributionValue) || "—", icon: <StarIcon size={13} />, color: "#0F766E" },
            { label: "פעילויות", value: totalActivities, icon: <ActivityIcon size={13} />, color: "#10B981" },
          ].map((s, i) => (
            <div key={i} style={{
              padding: "14px 8px", textAlign: "center",
              borderLeft: i < 4 ? `1px solid ${BORDER_LIGHT}` : "none",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, color: s.color, marginBottom: 4 }}>
                {s.icon}
                <span style={{ fontSize: 18, fontWeight: 800, fontFamily: "Assistant" }}>{s.value}</span>
              </div>
              <div style={{ fontSize: 11, color: TEXT_MUTED, fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div style={{ display: "flex", gap: 8, padding: "14px 24px", borderBottom: `1px solid ${BORDER}` }}>
          {telHref && (
            <a href={telHref} style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              padding: "10px 0", borderRadius: 10, background: "#EFF6FF", color: "#2563EB",
              textDecoration: "none", fontSize: 13, fontWeight: 600, fontFamily: "Heebo",
              border: "1px solid #DBEAFE",
            }}>
              <Ic.Phone /> התקשרות
            </a>
          )}
          {waHref && (
            <a href={waHref} target="_blank" rel="noreferrer" style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              padding: "10px 0", borderRadius: 10, background: "#F0FDF4", color: "#16A34A",
              textDecoration: "none", fontSize: 13, fontWeight: 600, fontFamily: "Heebo",
              border: "1px solid #DCFCE7",
            }}>
              <WhatsAppIcon size={14} /> וואטסאפ
            </a>
          )}
          <button
            onClick={() => onView(volunteer.id)}
            style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              padding: "10px 0", borderRadius: 10, background: ACCENT_LIGHT, color: ACCENT,
              border: `1px solid #FECDD3`, fontSize: 13, fontWeight: 600, fontFamily: "Heebo",
              cursor: "pointer",
            }}
          >
            <Ic.Eye /> כרטיס מלא
          </button>
        </div>

        {/* Details */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 24px 24px" }}>
          <InfoRow icon={<Ic.Phone />} label="טלפון" value={volunteer.phone} />
          <InfoRow icon={<MailIcon />} label='דוא"ל' value={volunteer.email} />
          <InfoRow icon={<MapPinIcon />} label="עיר" value={volunteer.city} />
          <InfoRow icon={<RepeatIcon />} label="סוג התנדבות" value={volunteerModeLabel} />
          <InfoRow icon={<CalendarIcon />} label="יום הולדת עברי" value={volunteer.hebrewBirthday} />
          <InfoRow icon={<CalendarIcon />} label="תאריך הצטרפות" value={volunteer.joinDate} />
          <InfoRow icon={<StarIcon />} label="נקודות" value={volunteer.points || 0} />
          <InfoRow icon={<UsersIcon />} label="רכז משויך" value={coordinatorNames.length ? coordinatorNames.join(", ") : "—"} />
          <InfoRow icon={<UsersIcon />} label="ראש צוות" value={teamLeadNames.length ? teamLeadNames.join(", ") : "—"} />
          <InfoRow icon={<UsersIcon />} label="תפקיד בצוות" value={volunteer.isTeamLead === true ? "ראש צוות" : "מתנדב"} />
          <InfoRow icon={<span style={{ fontSize: 14 }}>🏷</span>} label="תחומי פעילות">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 2 }}>
              {(volunteer.domains || []).length > 0 ? (volunteer.domains || []).map(did => {
                const d = domainById[did];
                return d ? (
                  <span key={did} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <DomainTag domain={d} />
                    {(d.city || "").trim() && <span style={{ fontSize: 11, color: TEXT_SECONDARY }}>({d.city})</span>}
                  </span>
                ) : <span key={did} style={{ fontSize: 13 }}>{did}</span>;
              }) : <span style={{ fontSize: 13, color: TEXT_MUTED }}>—</span>}
            </div>
          </InfoRow>
          {volunteer.notes && (
            <InfoRow icon={<NoteIcon />} label="הערות" value={volunteer.notes} />
          )}
        </div>
      </div>
    </>
  );
}

/* ─── Main Page Component ────────────────────────────────────── */
export default function VolunteersPage({ volunteers, domains, activities, onView, onRefresh, showToast, isMobile = false, addOpenToken = 0, users = [], currentUserData = null, canManageUsers = false, canAddVolunteers = true, onQuickAddComplete }) {
  const PAGE_SIZE = 10;
  const [search, setSearch] = useState("");
  const [fMainDom, setFMainDom] = useState("all");
  const [fSubDom, setFSubDom] = useState("all");
  const [fCity, setFCity] = useState("all");
  const [fStat, setFStat] = useState("all");
  const [fHours, setFHours] = useState("all");
  const [fActs, setFActs] = useState("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [modal, setModal] = useState(null);
  const [importing, setImporting] = useState(false);
  const [drawerVol, setDrawerVol] = useState(null);
  const [openedFromQuickAction, setOpenedFromQuickAction] = useState(false);
  const fileInputRef = useRef(null);
  const listEndRef = useRef(null);

  const fullName = (v) => {
    const firstName = (v?.firstName || "").trim();
    const lastName = (v?.lastName || "").trim();
    const explicitName = (v?.name || "").trim();
    if (firstName || lastName) return `${lastName} ${firstName}`.trim();
    return explicitName;
  };
  const volunteerModeLabel = (v) => ((v?.engagementType || "fixed") === "fixed" ? "קבוע" : "מזדמן");
  const searchName = (v) => {
    const firstName = (v?.firstName || "").trim();
    const lastName = (v?.lastName || "").trim();
    const explicitName = (v?.name || "").trim();
    return `${explicitName} ${firstName} ${lastName} ${lastName} ${firstName}`.trim();
  };
  const normalizePhone = (phone) => `${phone || ""}`.replace(/[^\d+]/g, "");
  const toWhatsappNumber = (phone) => {
    const digits = `${phone || ""}`.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("972")) return digits;
    if (digits.startsWith("0")) return `972${digits.slice(1)}`;
    return digits;
  };
  const getPhoneLinks = (phone) => {
    const telRaw = normalizePhone(phone);
    const waRaw = toWhatsappNumber(phone);
    return {
      telHref: telRaw ? `tel:${telRaw}` : "",
      waHref: waRaw ? `https://wa.me/${waRaw}` : "",
    };
  };
  const allUsers = users.length ? users : (currentUserData ? [currentUserData] : []);
  const domainById = useMemo(() => {
    const map = {};
    domains.forEach((d) => { map[d.id] = d; });
    return map;
  }, [domains]);
  const isMainCategory = (d) => d?.isMainCategory !== false;
  const mainDomainOptions = useMemo(
    () => domains.filter((d) => d.active !== false && isMainCategory(d)),
    [domains]
  );
  const subDomainOptions = useMemo(
    () => domains.filter((d) => d.active !== false && (d.parentDomainId || "") === fMainDom),
    [domains, fMainDom]
  );
  const cityOptions = useMemo(() => {
    const seen = new Set();
    const rows = [];
    volunteers.forEach((v) => {
      const city = (v?.city || "").trim();
      if (!city) return;
      const key = city.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      rows.push(city);
    });
    return rows.sort((a, b) => a.localeCompare(b, "he"));
  }, [volunteers]);
  const resolveMainDomainId = (domainId) => {
    const current = domainById[domainId];
    if (!current) return "";
    const parentId = (current.parentDomainId || "").trim();
    if (!parentId) return current.id;
    return parentId;
  };
  const getVolunteerDomainInfo = (volunteerRow) => {
    const domainIds = (volunteerRow?.domains || []).filter(Boolean);
    const firstDomainId = domainIds[0] || "";
    const selected = domainById[firstDomainId];
    const labels = domainIds
      .map((id) => {
        const d = domainById[id];
        if (!d) return null;
        const parent = (d.parentDomainId || "").trim() ? domainById[(d.parentDomainId || "").trim()] : null;
        const city = (d.city || "").trim();
        const base = parent ? `${parent.name} / ${d.name}` : d.name;
        return `${base}${city ? ` (${city})` : ""}`;
      })
      .filter(Boolean);
    if (!selected) return { selected: null, parent: null, labels };
    const parentId = (selected.parentDomainId || "").trim();
    if (!parentId) return { selected, parent: null, labels };
    return { selected, parent: domainById[parentId] || null, labels };
  };
  const coordinators = allUsers.filter((u) => u.role === "coordinator");
  const groupCoordinators = allUsers.filter((u) => u.role === "group_coordinator"); // legacy only
  const usersById = useMemo(() => {
    const m = new Map();
    allUsers.forEach((u) => m.set(u.id, u));
    return m;
  }, [allUsers]);

  const normalizedSearch = (search || "").trim().toLowerCase();
  const statsByVolunteerId = useMemo(() => {
    const monthCutoff = new Date();
    monthCutoff.setDate(monthCutoff.getDate() - 30);
    const map = {};
    volunteers.forEach((v) => {
        map[v.id] = { monthHours: 0, activitiesCount: 0, contributionsTotal: 0, contributionValueTotal: 0 };
    });
    activities.forEach((a) => {
      const vid = (a.volunteerId || "").trim();
      if (!vid || !map[vid]) return;
      map[vid].activitiesCount += 1;
      map[vid].contributionsTotal += getActivityContributionTotal(a);
      map[vid].contributionValueTotal += getActivityContributionValueTotal(a);
      if (a.date && new Date(a.date) >= monthCutoff) {
        map[vid].monthHours += Number(a.duration || 0);
      }
    });
    return map;
  }, [volunteers, activities]);
  const filtered = useMemo(() => (
    volunteers
      .filter((v) => {
        const n = fullName(v);
        const stats = statsByVolunteerId[v.id] || { monthHours: 0, activitiesCount: 0, contributionsTotal: 0 };
        if (normalizedSearch) {
          const hay = [n || "", searchName(v), v.phone || "", v.city || "", v.email || ""].join(" ").toLowerCase();
          if (!hay.includes(normalizedSearch)) return false;
        }
        if (fMainDom !== "all") {
          const volDomains = v.domains || [];
          if (fSubDom !== "all") {
            if (!volDomains.includes(fSubDom)) return false;
          } else {
            const hasMain = volDomains.some((did) => resolveMainDomainId(did) === fMainDom);
            if (!hasMain) return false;
          }
        }
        if (fStat !== "all" && v.status !== fStat) return false;
        if (fCity !== "all" && (v.city || "").trim() !== fCity) return false;
        if (fHours === "zero" && stats.monthHours > 0) return false;
        if (fHours === "low" && !(stats.monthHours > 0 && stats.monthHours <= 5)) return false;
        if (fHours === "mid" && !(stats.monthHours > 5 && stats.monthHours <= 20)) return false;
        if (fHours === "high" && !(stats.monthHours > 20)) return false;
        if (fActs === "none" && stats.activitiesCount > 0) return false;
        if (fActs === "few" && !(stats.activitiesCount >= 1 && stats.activitiesCount <= 5)) return false;
        if (fActs === "many" && !(stats.activitiesCount >= 6)) return false;
        return true;
      })
      .sort((a, b) => fullName(a).localeCompare(fullName(b), "he"))
  ), [volunteers, normalizedSearch, fMainDom, fSubDom, fCity, fStat, fHours, fActs, statsByVolunteerId]);
  const isSearching = normalizedSearch.length > 0;
  const displayedVolunteers = isSearching ? filtered : filtered.slice(0, visibleCount);
  const hasMore = !isSearching && visibleCount < filtered.length;

  const getMonthHours = (vid) => Number(statsByVolunteerId[vid]?.monthHours || 0);
  const getActivitiesCount = (vid) => Number(statsByVolunteerId[vid]?.activitiesCount || 0);
  const getContributionsTotal = (vid) => Number(statsByVolunteerId[vid]?.contributionsTotal || 0);
  const getContributionValueTotal = (vid) => Number(statsByVolunteerId[vid]?.contributionValueTotal || 0);

  const coordinatorLookup = useMemo(() => {
    const map = new Map();
    const norm = (v) => (v || "").trim().toLowerCase();
    coordinators.forEach((c) => {
      if (!c) return;
      const cid = (c.id || "").trim();
      if (cid) map.set(norm(cid), cid);
      if (c.email) map.set(norm(c.email), cid);
      if (c.name) map.set(norm(c.name), cid);
    });
    return map;
  }, [coordinators]);
  const groupCoordinatorLookup = useMemo(() => {
    const map = new Map();
    const norm = (v) => (v || "").trim().toLowerCase();
    groupCoordinators.forEach((c) => {
      if (!c) return;
      const cid = (c.id || "").trim();
      if (cid) map.set(norm(cid), cid);
      if (c.email) map.set(norm(c.email), cid);
      if (c.name) map.set(norm(c.name), cid);
    });
    return map;
  }, [groupCoordinators]);

  const readCell = (row, aliases) => {
    const norm = (v) => (v || "").toString().trim().toLowerCase().replace(/\s+/g, " ");
    const keyMap = new Map(Object.keys(row || {}).map((k) => [norm(k), row[k]]));
    for (const alias of aliases) {
      const value = keyMap.get(norm(alias));
      if (value !== undefined && value !== null && `${value}`.trim() !== "") return `${value}`.trim();
    }
    return "";
  };

  const normalizeImportDate = (value) => {
    const txt = `${value || ""}`.trim();
    if (!txt) return new Date().toISOString().split("T")[0];
    const m = txt.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
    if (m) {
      const d = String(m[1]).padStart(2, "0");
      const mo = String(m[2]).padStart(2, "0");
      const yy = m[3].length === 2 ? `20${m[3]}` : m[3];
      return `${yy}-${mo}-${d}`;
    }
    const iso = txt.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) return txt;
    return new Date().toISOString().split("T")[0];
  };

  const runVolunteersImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
      if (!rows.length) {
        await showStyledAlert("הקובץ ריק או שאין בו שורות נתונים.");
        return;
      }
      let created = 0, skipped = 0, unresolvedCoordinator = 0, unresolvedGroupCoordinator = 0;
      for (const row of rows) {
        const firstName = readCell(row, ["שם פרטי", "first name", "firstname"]);
        const lastName = readCell(row, ["שם משפחה", "משפחה", "last name", "lastname", "surname"]);
        const fullNameFromParts = `${firstName} ${lastName}`.trim();
        const name = readCell(row, ["שם", "שם מלא", "name"]) || fullNameFromParts;
        const finalFirst = firstName || (name.split(" ")[0] || "");
        const finalLast = lastName || name.split(" ").slice(1).join(" ");
        if (!name) { skipped += 1; continue; }
        const coordinatorRaw = readCell(row, ["רכז משויך", "רכז", "רכזת משוייכת", "רכזת משויכת", "coordinator", "coordinator email", "רכז אימייל"]);
        const groupCoordinatorRaw = readCell(row, ["רכז קבוצה", "רכזת קבוצה", "group coordinator", "group_coordinator", "רכז קבוצתי"]);
        const resolvedGroupCoordinatorId = groupCoordinatorRaw ? (groupCoordinatorLookup.get(groupCoordinatorRaw.trim().toLowerCase()) || "") : "";
        const groupParentCoordinatorId = resolvedGroupCoordinatorId ? (usersById.get(resolvedGroupCoordinatorId)?.parentCoordinatorId || "") : "";
        let resolvedCoordinatorId = coordinatorRaw ? (coordinatorLookup.get(coordinatorRaw.trim().toLowerCase()) || "") : "";
        if (!resolvedCoordinatorId && groupParentCoordinatorId) resolvedCoordinatorId = groupParentCoordinatorId;
        if (coordinatorRaw && !resolvedCoordinatorId) unresolvedCoordinator += 1;
        if (groupCoordinatorRaw && !resolvedGroupCoordinatorId) unresolvedGroupCoordinator += 1;
        const street = readCell(row, ["רחוב", "כתובת", "address", "street"]);
        const freeNotes = readCell(row, ["הערות", "notes", "הערות מעמודה a ואילך", "הערות מעומדה a ואילך"]);
        const birthdayValue = readCell(row, ["ת.לידה", "תאריך לידה", "birthdate", "date of birth"]);
        await addVolunteer({
          name, firstName: finalFirst, lastName: finalLast,
          phone: readCell(row, ["טלפון", "נייד", "ניד", "phone", "mobile"]),
          city: readCell(row, ["עיר", "city"]),
          email: readCell(row, ["אימייל", "דואל", "email"]),
          birthDate: birthdayValue || "",
          coordinatorId: resolvedCoordinatorId || "",
          groupCoordinatorId: resolvedGroupCoordinatorId || "",
          status: "active", domains: [], hebrewBirthday: "", points: 0,
          joinDate: normalizeImportDate(readCell(row, ["תאריך הצטרפות", "join date", "joinDate"])),
          notes: [street ? `רחוב: ${street}` : "", freeNotes || ""].filter(Boolean).join(" | "),
        });
        created += 1;
      }
      await onRefresh();
      showToast(`ייבוא מתנדבים הושלם: ${created} נוספו`);
      await showStyledAlert(
        `הייבוא הסתיים.\nנוספו: ${created}\nדולגו (ללא שם): ${skipped}\nרכז לא נמצא והושאר ריק: ${unresolvedCoordinator}`
        + `\nראש צוות (עמודת מורשת) לא נמצא והושאר ריק: ${unresolvedGroupCoordinator}`
      );
    } catch (e) {
      await showStyledAlert("שגיאה בייבוא מתנדבים: " + e.message);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const showImportInstructions = async () => {
    await showStyledAlert(
      "הוראות ייבוא מתנדבים מאקסל:\n\n" +
      "1) יש להשתמש בגיליון הראשון בקובץ.\n" +
      "2) כותרות מומלצות: שם פרטי, משפחה/שם משפחה, נייד/ניד/טלפון, רחוב, עיר, ת.לידה, רכזת משוייכת, רכזת קבוצה, הערות.\n" +
      "   אופציונלי: ראש צוות (או עמודת מורשת 'רכז קבוצה').\n" +
      "   אפשר גם עמודת 'שם מלא' במקום שם פרטי/משפחה.\n" +
      "3) חובה שדה שם. שורות ללא שם ידולגו.\n" +
      "4) רכז משויך מזוהה לפי שם/אימייל/מזהה רכז. אם לא נמצא - יישאר ריק.\n" +
      "5) אפשר קובץ .xlsx / .xls / .csv"
    );
  };

  const getRating = (vol) => {
    const hours = activities.filter(a => a.volunteerId === vol.id).reduce((s, a) => s + Number(a.duration), 0);
    const acts = getActivitiesCount(vol.id);
    const points = Number(vol.points || 0);
    return Math.round(points + (hours * 2) + (acts * 5));
  };

  const handleSave = async (form) => {
    try {
      if (modal === "add") {
        await addVolunteer(form);
        showToast("מתנדב נוסף בהצלחה");
        if (openedFromQuickAction && typeof onQuickAddComplete === "function") {
          setOpenedFromQuickAction(false);
          onQuickAddComplete();
        }
      } else {
        await updateVolunteer(modal.data.id, form);
        showToast("פרטים עודכנו");
      }
      setModal(null);
      onRefresh();
    } catch (e) {
      await showStyledAlert("שגיאה: " + e.message);
    }
  };
  const handleDelete = async (volunteerId) => {
    if (!await showStyledConfirm("למחוק את המתנדב?")) return;
    try {
      await deleteVolunteer(volunteerId);
      showToast("המתנדב נמחק");
      setDrawerVol(null);
      onRefresh();
    } catch (e) {
      await showStyledAlert("שגיאה במחיקת מתנדב: " + e.message);
    }
  };

  useEffect(() => {
    if (addOpenToken > 0) {
      setOpenedFromQuickAction(true);
      setModal("add");
    }
  }, [addOpenToken]);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [search, fMainDom, fSubDom, fCity, fStat, fHours, fActs, volunteers.length]);
  useEffect(() => {
    if (fMainDom === "all") {
      if (fSubDom !== "all") setFSubDom("all");
      return;
    }
    if (!subDomainOptions.some((d) => d.id === fSubDom)) {
      setFSubDom("all");
    }
  }, [fMainDom, fSubDom, subDomainOptions]);
  useEffect(() => {
    if (!hasMore || !listEndRef.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setVisibleCount(prev => Math.min(prev + PAGE_SIZE, filtered.length));
    }, { rootMargin: "120px 0px" });
    observer.observe(listEndRef.current);
    return () => observer.disconnect();
  }, [hasMore, filtered.length]);

  /* ─── Status filter chips ──────────────────────────────────── */
  const StatusChip = ({ active, onClick, children, count }) => (
    <button onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "6px 14px", borderRadius: 20, fontSize: 13,
      background: active ? ACCENT_LIGHT : "white",
      border: `1.5px solid ${active ? ACCENT : BORDER}`,
      color: active ? ACCENT : TEXT_SECONDARY,
      cursor: "pointer", fontFamily: "Heebo", fontWeight: active ? 600 : 400,
      transition: "all 0.15s",
    }}>
      {children}
      {count !== undefined && (
        <span style={{
          fontSize: 11, fontWeight: 700,
          background: active ? ACCENT : "#E5E7EB",
          color: active ? "white" : TEXT_SECONDARY,
          borderRadius: 10, padding: "1px 7px", lineHeight: "16px",
        }}>{count}</span>
      )}
    </button>
  );

  const handleRowClick = (v) => {
    setDrawerVol(v);
  };

  /* ─── Table: Compact columns (name, phone, city, domain, rating, status) ── */
  /* ─── Mobile: Clean cards ─────────────────────────────────── */

  return (
    <div>
      {/* ── Toolbar ──────────────────────────────────────────── */}
      <div style={{
        display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center",
        justifyContent: "space-between",
      }}>
        {/* Left side: search + filters */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", flex: 1 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: BG_SUBTLE, borderRadius: 10, padding: "8px 14px",
            width: isMobile ? "100%" : 200, border: `1px solid ${BORDER}`,
            transition: "border-color 0.15s",
          }}>
            <Ic.Search />
            <input
              placeholder="חיפוש..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ border: "none", background: "none", outline: "none", fontSize: 14, width: "100%", direction: "rtl", fontFamily: "Heebo" }}
            />
          </div>
          {!isMobile && (
            <>
              <StatusChip active={fStat === "all"} onClick={() => setFStat("all")} count={volunteers.length}>הכל</StatusChip>
              <StatusChip active={fStat === "active"} onClick={() => setFStat("active")}>פעילים</StatusChip>
              <StatusChip active={fStat === "inactive"} onClick={() => setFStat("inactive")}>לא פעילים</StatusChip>
              <span style={{ width: 1, height: 24, background: BORDER }} />
              <select
                value={fMainDom}
                onChange={(e) => setFMainDom(e.target.value)}
                style={{ ...inputStyle, width: 180, padding: "8px 12px", cursor: "pointer", fontSize: 13 }}
              >
                <option value="all">כל הקטגוריות הראשיות</option>
                {mainDomainOptions.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <select
                value={fSubDom}
                onChange={(e) => setFSubDom(e.target.value)}
                disabled={fMainDom === "all"}
                style={{ ...inputStyle, width: 180, padding: "8px 12px", cursor: fMainDom === "all" ? "not-allowed" : "pointer", fontSize: 13, opacity: fMainDom === "all" ? 0.7 : 1 }}
              >
                <option value="all">כל תתי התחומים</option>
                {subDomainOptions.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <select
                value={fCity}
                onChange={(e) => setFCity(e.target.value)}
                style={{ ...inputStyle, width: 170, padding: "8px 12px", cursor: "pointer", fontSize: 13 }}
              >
                <option value="all">ערים: הכל</option>
                {cityOptions.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              <select
                value={fHours}
                onChange={(e) => setFHours(e.target.value)}
                style={{ ...inputStyle, width: 170, padding: "8px 12px", cursor: "pointer", fontSize: 13 }}
              >
                <option value="all">שעות חודשיות: הכל</option>
                <option value="zero">0 שעות</option>
                <option value="low">0-5 שעות</option>
                <option value="mid">5-20 שעות</option>
                <option value="high">20+ שעות</option>
              </select>
              <select
                value={fActs}
                onChange={(e) => setFActs(e.target.value)}
                style={{ ...inputStyle, width: 170, padding: "8px 12px", cursor: "pointer", fontSize: 13 }}
              >
                <option value="all">פעילויות: הכל</option>
                <option value="none">ללא פעילויות</option>
                <option value="few">1-5 פעילויות</option>
                <option value="many">6+ פעילויות</option>
              </select>
            </>
          )}
        </div>
        {/* Right side: action buttons */}
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button style={iconBtn} onClick={showImportInstructions} title="הוראות ייבוא"><InfoIcon /></button>
          <button style={iconBtn} onClick={() => fileInputRef.current?.click()} disabled={importing} title="ייבוא מאקסל">
            {importing ? <span style={{ fontSize: 12 }}>...</span> : <UploadIcon />}
          </button>
          {canAddVolunteers && <button style={iconBtnAccent} onClick={() => { setOpenedFromQuickAction(false); setModal("add"); }} title="הוספת מתנדב"><UserPlusIcon size={17} /></button>}
        </div>
      </div>

      {/* Mobile-only filter row */}
      {isMobile && (
        <div style={{ display: "flex", gap: 6, marginBottom: 12, overflowX: "auto", paddingBottom: 4 }}>
          <StatusChip active={fStat === "all"} onClick={() => setFStat("all")} count={volunteers.length}>הכל</StatusChip>
          <StatusChip active={fStat === "active"} onClick={() => setFStat("active")}>פעילים</StatusChip>
          <StatusChip active={fStat === "inactive"} onClick={() => setFStat("inactive")}>לא פעילים</StatusChip>
          <select
            value={fMainDom}
            onChange={(e) => setFMainDom(e.target.value)}
            style={{ ...inputStyle, minWidth: 140, padding: "6px 10px", cursor: "pointer", fontSize: 12, borderRadius: 20 }}
          >
            <option value="all">ראשיות: הכל</option>
            {mainDomainOptions.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <select
            value={fSubDom}
            onChange={(e) => setFSubDom(e.target.value)}
            disabled={fMainDom === "all"}
            style={{ ...inputStyle, minWidth: 130, padding: "6px 10px", cursor: fMainDom === "all" ? "not-allowed" : "pointer", fontSize: 12, borderRadius: 20, opacity: fMainDom === "all" ? 0.7 : 1 }}
          >
            <option value="all">תתי: הכל</option>
            {subDomainOptions.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <select
            value={fCity}
            onChange={(e) => setFCity(e.target.value)}
            style={{ ...inputStyle, minWidth: 120, padding: "6px 10px", cursor: "pointer", fontSize: 12, borderRadius: 20 }}
          >
            <option value="all">ערים: הכל</option>
            {cityOptions.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
          <select
            value={fHours}
            onChange={(e) => setFHours(e.target.value)}
            style={{ ...inputStyle, minWidth: 130, padding: "6px 10px", cursor: "pointer", fontSize: 12, borderRadius: 20 }}
          >
            <option value="all">שעות: הכל</option>
            <option value="zero">0 שעות</option>
            <option value="low">0-5 שעות</option>
            <option value="mid">5-20 שעות</option>
            <option value="high">20+ שעות</option>
          </select>
          <select
            value={fActs}
            onChange={(e) => setFActs(e.target.value)}
            style={{ ...inputStyle, minWidth: 130, padding: "6px 10px", cursor: "pointer", fontSize: 12, borderRadius: 20 }}
          >
            <option value="all">פעילויות: הכל</option>
            <option value="none">ללא</option>
            <option value="few">1-5</option>
            <option value="many">6+</option>
          </select>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: "none" }}
        onChange={(e) => runVolunteersImport(e.target.files?.[0])}
      />

      {/* ── Results header ─────────────────────────────────── */}
      <div style={{
        background: "white", borderRadius: 14, border: `1px solid ${BORDER}`, overflow: "hidden",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: isMobile ? "12px 16px" : "14px 20px", borderBottom: `1px solid ${BORDER}`,
          background: BG_SUBTLE,
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_SECONDARY, fontFamily: "Heebo" }}>
            {filtered.length} מתנדבים
          </span>
          {isSearching && (
            <button onClick={() => setSearch("")} style={{ ...btnGhost, fontSize: 12, color: ACCENT }}>
              נקה חיפוש
            </button>
          )}
        </div>

        {/* ── Desktop table ────────────────────────────────── */}
        {!isMobile ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["שם", "טלפון", "עיר", "תחום", "דירוג", "סטטוס", ""].map(h => (
                  <th key={h} style={{
                    padding: "10px 16px", fontSize: 11, fontWeight: 600,
                    color: TEXT_MUTED, textAlign: "right", background: "white",
                    borderBottom: `1px solid ${BORDER}`,
                    textTransform: "uppercase", letterSpacing: "0.3px",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedVolunteers.map(v => {
                const domainInfo = getVolunteerDomainInfo(v);
                const rating = getRating(v);
                const links = getPhoneLinks(v.phone);
                const contributionsTotal = getContributionsTotal(v.id);
                const contributionValueTotal = getContributionValueTotal(v.id);
                return (
                  <tr
                    key={v.id}
                    style={{ cursor: "pointer", transition: "background 0.1s" }}
                    onClick={() => handleRowClick(v)}
                    onMouseEnter={e => e.currentTarget.style.background = BG_SUBTLE}
                    onMouseLeave={e => e.currentTarget.style.background = "white"}
                  >
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                          background: `linear-gradient(135deg, ${ACCENT}, #E8364F)`,
                          color: "white", display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 14, fontWeight: 700, fontFamily: "Assistant",
                        }}>
                          {fullName(v)?.[0]}
                        </div>
                        <div>
                          <span style={{ fontWeight: 600, fontSize: 14, color: TEXT_PRIMARY }}>{fullName(v)}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                            {v.city && <span style={{ fontSize: 12, color: TEXT_MUTED }}>{v.city}</span>}
                            <EngagementBadge type={v.engagementType || "fixed"} />
                            {contributionsTotal > 0 && <span style={{ fontSize: 11, color: "#9A3412" }}>{contributionsTotal} תרומות</span>}
                            {contributionValueTotal > 0 && <span style={{ fontSize: 11, color: "#0F766E" }}>{formatContributionCurrency(contributionValueTotal)}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 13, color: TEXT_SECONDARY, direction: "ltr" }}>{v.phone || "—"}</span>
                        {links.waHref && (
                          <a
                            href={links.waHref}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            title="וואטסאפ"
                            style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: 22, height: 22, borderRadius: 6,
                              border: "1px solid #DCFCE7", color: "#16A34A", background: "#F0FDF4",
                            }}
                          >
                            <WhatsAppIcon size={11} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: 13, color: TEXT_SECONDARY }}>
                      {v.city || "—"}
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: 13 }}>
                      {domainInfo.selected ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <DomainTag domain={domainInfo.selected} />
                          <span style={{ fontSize: 11, color: TEXT_SECONDARY }}>
                            {domainInfo.labels?.[0] || (domainInfo.parent ? `${domainInfo.parent.name} / ${domainInfo.selected.name}` : domainInfo.selected.name)}
                          </span>
                          {(domainInfo.labels?.length || 0) > 1 && (
                            <span style={{
                              display: "inline-flex", alignItems: "center", width: "fit-content",
                              padding: "1px 7px", borderRadius: 999, border: `1px solid ${BORDER}`,
                              background: BG_SUBTLE, color: TEXT_SECONDARY, fontSize: 10, fontWeight: 600,
                            }}>
                              +{domainInfo.labels.length - 1} תחומים נוספים
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: (getVolunteerCoordinatorIds(v).length || getVolunteerTeamLeadIds(v).length) ? "#B45309" : TEXT_MUTED, fontSize: 12 }}>
                          {(getVolunteerCoordinatorIds(v).length || getVolunteerTeamLeadIds(v).length) ? "משויך ללא תחום" : "—"}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <StarIcon size={12} />
                        <span style={{ fontFamily: "Assistant", fontWeight: 800, color: "#F59E0B", fontSize: 14 }}>{rating}</span>
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                      <StatusBadge status={v.status} />
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                      <div style={{ display: "flex", alignItems: "center", color: TEXT_MUTED }}>
                        <ChevronRightIcon size={16} />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: TEXT_MUTED }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                  לא נמצאו מתנדבים
                </td></tr>
              )}
            </tbody>
          </table>
        ) : (
          /* ── Mobile cards ──────────────────────────────────── */
          <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
            {displayedVolunteers.map((v) => {
              const domainInfo = getVolunteerDomainInfo(v);
              const rating = getRating(v);
              const links = getPhoneLinks(v.phone);
              const contributionsTotal = getContributionsTotal(v.id);
              const contributionValueTotal = getContributionValueTotal(v.id);
              return (
                <div
                  key={v.id}
                  onClick={() => handleRowClick(v)}
                  style={{
                    border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14,
                    cursor: "pointer", background: "white",
                    transition: "box-shadow 0.15s, transform 0.15s",
                    display: "flex", alignItems: "center", gap: 12,
                  }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                    background: `linear-gradient(135deg, ${ACCENT}, #E8364F)`,
                    color: "white", display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 17, fontWeight: 700, fontFamily: "Assistant",
                  }}>
                    {fullName(v)?.[0]}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: TEXT_PRIMARY, fontFamily: "Assistant" }}>{fullName(v)}</span>
                      <StatusBadge status={v.status} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: TEXT_SECONDARY, marginBottom: 6 }}>
                      {v.city && <span>{v.city}</span>}
                      {v.city && v.phone && <span style={{ color: BORDER }}>|</span>}
                      {v.phone && <span style={{ direction: "ltr" }}>{v.phone}</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <EngagementBadge type={v.engagementType || "fixed"} />
                      {domainInfo.selected && <DomainTag domain={domainInfo.selected} />}
                      {!domainInfo.selected && (getVolunteerCoordinatorIds(v).length || getVolunteerTeamLeadIds(v).length) && (
                        <span style={{ fontSize: 11, color: "#B45309", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 999, padding: "2px 8px" }}>
                          משויך ללא תחום
                        </span>
                      )}
                      {domainInfo.selected && (
                        <span style={{ fontSize: 11, color: TEXT_SECONDARY }}>
                          {domainInfo.labels?.[0]}
                          {(domainInfo.labels?.length || 0) > 1 ? ` +${domainInfo.labels.length - 1}` : ""}
                        </span>
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <StarIcon size={11} />
                        <span style={{ fontWeight: 700, color: "#F59E0B", fontSize: 13 }}>{rating}</span>
                      </div>
                      {contributionsTotal > 0 && (
                        <span style={{ fontSize: 11, color: "#9A3412", fontWeight: 700 }}>
                          {contributionsTotal} תרומות
                        </span>
                      )}
                      {contributionValueTotal > 0 && (
                        <span style={{ fontSize: 11, color: "#0F766E", fontWeight: 700 }}>
                          {formatContributionCurrency(contributionValueTotal)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quick actions */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {links.telHref && (
                      <a href={links.telHref} style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 32, height: 32, borderRadius: 9,
                        border: "1px solid #DBEAFE", color: "#2563EB", background: "#EFF6FF",
                      }}>
                        <Ic.Phone />
                      </a>
                    )}
                    {links.waHref && (
                      <a href={links.waHref} target="_blank" rel="noreferrer" style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 32, height: 32, borderRadius: 9,
                        border: "1px solid #DCFCE7", color: "#16A34A", background: "#F0FDF4",
                      }}>
                        <WhatsAppIcon size={14} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ textAlign: "center", padding: 40, color: TEXT_MUTED }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                לא נמצאו מתנדבים
              </div>
            )}
          </div>
        )}
      </div>

      {/* Load more */}
      {hasMore && (
        <div ref={listEndRef} style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
          <button
            style={{ ...iconBtnWithLabel, color: ACCENT, border: `1px solid ${ACCENT}20` }}
            onClick={() => setVisibleCount(prev => Math.min(prev + PAGE_SIZE, filtered.length))}
          >
            טען עוד ({Math.min(PAGE_SIZE, filtered.length - displayedVolunteers.length)})
          </button>
        </div>
      )}

      {/* Drawer */}
      {drawerVol && (
        <VolunteerDrawer
          volunteer={drawerVol}
          onClose={() => setDrawerVol(null)}
          onView={onView}
          onEdit={(v) => { setDrawerVol(null); setModal({ type: "edit", data: v }); }}
          onDelete={handleDelete}
          domains={domains}
          coordinators={coordinators}
          volunteers={volunteers}
          activities={activities}
          isMobile={isMobile}
        />
      )}

      {/* Form Modal */}
      {(modal === "add" || modal?.type === "edit") && (
        <VolunteerFormModal
          domains={domains}
          coordinators={coordinators}
          groupCoordinators={groupCoordinators}
          volunteers={volunteers}
          currentUserData={currentUserData}
          canManageUsers={canManageUsers}
          volunteer={modal?.data || null}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

/* ─── Form Modal (unchanged logic, slightly cleaner layout) ─── */
function VolunteerFormModal({ domains, coordinators, groupCoordinators = [], volunteers = [], currentUserData, canManageUsers, volunteer, onClose, onSave }) {
  const existingName = volunteer?.name || "";
  const inferredFirst = existingName.trim().split(" ")[0] || "";
  const inferredLast = existingName.trim().split(" ").slice(1).join(" ");
  const currentRole = currentUserData?.role || "";
  const isCurrentGroupCoordinator = currentRole === "group_coordinator";
  const isCurrentCoordinator = currentRole === "coordinator";
  const currentUserId = currentUserData?.id || "";
  const currentParentCoordinatorId = currentUserData?.parentCoordinatorId || "";
  const defaultCoordinatorIds = getVolunteerCoordinatorIds(volunteer).length
    ? getVolunteerCoordinatorIds(volunteer)
    : (isCurrentGroupCoordinator ? [currentParentCoordinatorId] : (isCurrentCoordinator ? [currentUserId] : []));
  const defaultGroupCoordinatorId = volunteer?.groupCoordinatorId || (isCurrentGroupCoordinator ? currentUserId : "");
  const defaultTeamLeadIds = getVolunteerTeamLeadIds(volunteer);
  const [f, setF] = useState({
    firstName: volunteer?.firstName || inferredFirst,
    lastName: volunteer?.lastName || inferredLast,
    name: volunteer?.name || existingName,
    phone: volunteer?.phone || "",
    city: volunteer?.city || "",
    email: volunteer?.email || "",
    hebrewBirthday: volunteer?.hebrewBirthday || "",
    joinDate: volunteer?.joinDate || new Date().toISOString().split("T")[0],
    points: volunteer?.points || 0,
    engagementType: volunteer?.engagementType || "fixed",
    domains: volunteer?.domains || [],
    status: volunteer?.status || "active",
    coordinatorIds: defaultCoordinatorIds.filter(Boolean).slice(0, 2),
    isTeamLead: volunteer?.isTeamLead === true,
    teamLeadVolunteerIds: defaultTeamLeadIds.filter(Boolean).slice(0, 2),
    groupCoordinatorId: defaultGroupCoordinatorId,
    notes: volunteer?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [selectedCoordinatorId, setSelectedCoordinatorId] = useState("");
  const [selectedTeamLeadId, setSelectedTeamLeadId] = useState("");
  const u = (k, v) => setF(p => {
    const next = { ...p, [k]: v };
    if (k === "firstName" || k === "lastName") {
      next.name = `${next.firstName || ""} ${next.lastName || ""}`.trim();
    }
    return next;
  });

  const handleSubmit = async () => {
    setSaving(true);
    const payload = { ...f };
    payload.coordinatorIds = (payload.coordinatorIds || []).filter(Boolean).slice(0, 2);
    payload.teamLeadVolunteerIds = (payload.teamLeadVolunteerIds || []).filter((id) => id && id !== (volunteer?.id || "")).slice(0, 2);
    payload.coordinatorId = payload.coordinatorIds[0] || "";
    payload.teamLeadVolunteerId = payload.teamLeadVolunteerIds[0] || "";
    if (payload.isTeamLead === true && !payload.coordinatorIds.length) {
      setSaving(false);
      await showStyledAlert("ראש צוות חייב להיות משויך לרכז.");
      return;
    }
    if (!payload.isTeamLead && payload.teamLeadVolunteerIds.length && !payload.coordinatorIds.length) {
      setSaving(false);
      await showStyledAlert("יש לבחור רכז לפני שיוך לראש צוות.");
      return;
    }
    if (isCurrentGroupCoordinator) {
      payload.coordinatorIds = [currentParentCoordinatorId || payload.coordinatorIds[0] || ""].filter(Boolean);
      payload.coordinatorId = payload.coordinatorIds[0] || "";
      payload.groupCoordinatorId = currentUserId;
    } else if (isCurrentCoordinator && !canManageUsers) {
      payload.coordinatorIds = [currentUserId];
      payload.coordinatorId = currentUserId;
      const allowedGroupIds = new Set(
        groupCoordinators.filter(g => (g.parentCoordinatorId || "") === currentUserId).map(g => g.id)
      );
      if (!allowedGroupIds.has(payload.groupCoordinatorId || "")) payload.groupCoordinatorId = "";
    }
    await onSave(payload);
    setSaving(false);
  };

  const availableTeamLeads = useMemo(() => {
    const selfId = volunteer?.id || "";
    return volunteers
      .filter((v) => v.isTeamLead === true)
      .filter((v) => v.id !== selfId)
      .filter((v) => v.status === "active")
      .filter((v) => !f.coordinatorIds?.length || getVolunteerCoordinatorIds(v).some((id) => f.coordinatorIds.includes(id)));
  }, [volunteers, volunteer?.id, f.coordinatorIds]);

  const availableGroupCoordinators = useMemo(() => {
    if (isCurrentGroupCoordinator) return groupCoordinators.filter(g => g.id === currentUserId);
    if (!canManageUsers && isCurrentCoordinator) return groupCoordinators.filter(g => (g.parentCoordinatorId || "") === currentUserId);
    const primaryCoordinatorId = f.coordinatorIds?.[0] || "";
    if (!primaryCoordinatorId) return groupCoordinators;
    return groupCoordinators.filter(g => (g.parentCoordinatorId || "") === primaryCoordinatorId);
  }, [groupCoordinators, isCurrentGroupCoordinator, canManageUsers, isCurrentCoordinator, currentUserId, f.coordinatorIds]);

  const activeDomains = useMemo(() => domains.filter(d => d.active !== false), [domains]);
  const domainById = useMemo(() => { const m = {}; activeDomains.forEach(d => m[d.id] = d); return m; }, [activeDomains]);
  const mainDomains = useMemo(() => activeDomains.filter(d => d.isMainCategory !== false), [activeDomains]);
  const childrenByMainId = useMemo(() => {
    const map = {};
    activeDomains.forEach(d => {
      const pid = (d.parentDomainId || "").trim();
      if (!pid) return;
      if (!map[pid]) map[pid] = [];
      map[pid].push(d);
    });
    return map;
  }, [activeDomains]);

  const resolveMainDomainId = (domainId) => {
    const current = domainById[domainId];
    if (!current) return "";
    const parentId = (current.parentDomainId || "").trim();
    if (!parentId) return current.id;
    return domainById[parentId] ? parentId : current.id;
  };
  const domainsOverlapByHierarchy = (leftId, rightId) => {
    const a = (leftId || "").trim();
    const b = (rightId || "").trim();
    if (!a || !b) return false;
    if (a === b) return true;
    const aMain = resolveMainDomainId(a);
    const bMain = resolveMainDomainId(b);
    return !!aMain && !!bMain && aMain === bMain;
  };
  const coordinatorMatchesVolunteerDomains = (coordinatorUser, volunteerDomainIds) => {
    const managedDomainIds = Array.isArray(coordinatorUser?.managedDomainIds)
      ? coordinatorUser.managedDomainIds.map((id) => (id || "").trim()).filter(Boolean)
      : [];
    if (!managedDomainIds.length) return true; // legacy coordinators without scoped domains
    const volunteerDomainList = Array.isArray(volunteerDomainIds)
      ? volunteerDomainIds.map((id) => (id || "").trim()).filter(Boolean)
      : [];
    if (!volunteerDomainList.length) return true; // allow assigning coordinator before domains are defined
    return volunteerDomainList.some((volDomainId) =>
      managedDomainIds.some((managedId) => domainsOverlapByHierarchy(volDomainId, managedId))
    );
  };

  const initialAssignedDomainId = (volunteer?.domains || [])[0] || "";
  const [selectedMainDomainId, setSelectedMainDomainId] = useState(resolveMainDomainId(initialAssignedDomainId) || mainDomains[0]?.id || "");
  const [selectedChildDomainId, setSelectedChildDomainId] = useState(() => {
    if (!initialAssignedDomainId) return "";
    const resolvedMain = resolveMainDomainId(initialAssignedDomainId);
    return resolvedMain && initialAssignedDomainId !== resolvedMain ? initialAssignedDomainId : "";
  });
  const availableChildDomains = useMemo(() => childrenByMainId[selectedMainDomainId] || [], [childrenByMainId, selectedMainDomainId]);

  // Domains filtered by selected coordinator's managed domains
  const primaryCoordinatorId = f.coordinatorIds?.[0] || "";
  const selectedCoordinator = useMemo(() =>
    coordinators.find(c => c.id === primaryCoordinatorId) || null,
    [coordinators, primaryCoordinatorId]);
  const coordinatorManagedIds = useMemo(() => {
    if (!selectedCoordinator) return null;
    const ids = Array.isArray(selectedCoordinator.managedDomainIds)
      ? selectedCoordinator.managedDomainIds.map(id => (id || "").trim()).filter(Boolean)
      : [];
    return ids.length > 0 ? ids : null;
  }, [selectedCoordinator]);
  const visibleMainDomains = useMemo(() => {
    if (!coordinatorManagedIds) return mainDomains;
    return mainDomains.filter(main => {
      if (coordinatorManagedIds.includes(main.id)) return true;
      return (childrenByMainId[main.id] || []).some(child => coordinatorManagedIds.includes(child.id));
    });
  }, [mainDomains, coordinatorManagedIds, childrenByMainId]);
  const visibleChildDomains = useMemo(() => {
    if (!coordinatorManagedIds) return availableChildDomains;
    return availableChildDomains.filter(child => coordinatorManagedIds.includes(child.id));
  }, [availableChildDomains, coordinatorManagedIds]);

  const availableCoordinators = useMemo(() => {
    let base = coordinators;
    if (isCurrentGroupCoordinator) base = coordinators.filter((c) => c.id === currentParentCoordinatorId);
    if (!canManageUsers && isCurrentCoordinator) base = coordinators.filter((c) => c.id === currentUserId);
    return base.filter((c) => coordinatorMatchesVolunteerDomains(c, f.domains || []));
  }, [coordinators, isCurrentGroupCoordinator, currentParentCoordinatorId, canManageUsers, isCurrentCoordinator, currentUserId, f.domains]);
  const cityOptions = useMemo(() => Array.from(new Set(
    [
      ...volunteers.map((item) => (item?.city || "").trim()),
      ...domains.map((item) => (item?.city || "").trim()),
    ].filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, "he")), [domains, volunteers]);

  useEffect(() => {
    if (!selectedMainDomainId && mainDomains.length) { setSelectedMainDomainId(mainDomains[0].id); return; }
    if (selectedMainDomainId && !mainDomains.some(d => d.id === selectedMainDomainId)) {
      setSelectedMainDomainId(mainDomains[0]?.id || "");
      setSelectedChildDomainId("");
    }
  }, [selectedMainDomainId, mainDomains]);

  useEffect(() => {
    if (!(f.coordinatorIds || []).length) return;
    const allowedIds = new Set(availableCoordinators.map((c) => c.id));
    const nextCoordinatorIds = (f.coordinatorIds || []).filter((id) => allowedIds.has(id));
    if (nextCoordinatorIds.length === (f.coordinatorIds || []).length) return;
    setF((p) => ({ ...p, coordinatorIds: nextCoordinatorIds, teamLeadVolunteerIds: [] }));
  }, [availableCoordinators, f.coordinatorIds]);


  const addSelectedDomainToVolunteer = () => {
    const pickedId = selectedChildDomainId || selectedMainDomainId;
    if (!pickedId) return;
    setF(p => p.domains.includes(pickedId) ? p : { ...p, domains: [...p.domains, pickedId] });
  };
  const removeDomainFromVolunteer = (domainId) => {
    setF(p => ({ ...p, domains: p.domains.filter(id => id !== domainId) }));
  };

  const importFromPhoneContact = async () => {
    try {
      if (!("contacts" in navigator) || !navigator.contacts?.select) {
        await showStyledAlert("הדפדפן/מכשיר לא תומך בגישה לאנשי קשר.");
        return;
      }
      const picked = await navigator.contacts.select(["name", "tel", "email"], { multiple: false });
      if (!picked || !picked.length) return;
      const c = picked[0];
      const fullName = getContactValue(c?.name);
      const phone = getContactValue(c?.tel);
      const email = getContactValue(c?.email);
      const first = fullName.split(" ")[0] || "";
      const last = fullName.split(" ").slice(1).join(" ");
      setF(p => ({
        ...p,
        firstName: first || p.firstName, lastName: last || p.lastName,
        name: `${first || p.firstName || ""} ${last || p.lastName || ""}`.trim(),
        phone: phone || p.phone, email: email || p.email,
      }));
    } catch (e) {
      await showStyledAlert("שגיאה בייבוא מאנשי קשר: " + e.message);
    }
  };

  const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: TEXT_SECONDARY, marginBottom: 6 };

  return (
    <Modal title={volunteer ? "עריכת מתנדב" : "הוספת מתנדב"} onClose={onClose} footer={
      <>
        <button style={{ ...btnPrimary, ...(!f.firstName && { opacity: 0.5, pointerEvents: "none" }) }} onClick={handleSubmit} disabled={saving}>
          <Ic.Check />{saving ? "שומר..." : "שמירה"}
        </button>
        <button style={btnSecondary} onClick={onClose}>ביטול</button>
      </>
    }>
      {!volunteer && (
        <div style={{ marginBottom: 12 }}>
          <button type="button" style={btnSecondary} onClick={importFromPhoneContact}>ייבוא מאנשי קשר בטלפון</button>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>שם פרטי *</label>
          <input style={inputStyle} value={f.firstName} onChange={e => u("firstName", e.target.value)} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>שם משפחה</label>
          <input style={inputStyle} value={f.lastName} onChange={e => u("lastName", e.target.value)} />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>טלפון</label>
          <input style={{ ...inputStyle, direction: "ltr", textAlign: "right" }} value={f.phone} onChange={e => u("phone", e.target.value)} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>עיר</label>
          <AutocompleteTextInput value={f.city} onChange={(nextCity) => u("city", nextCity)} options={cityOptions} placeholder="לדוגמה: נתיבות" emptyText="אין ערים תואמות" />
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>דוא"ל</label>
        <input style={{ ...inputStyle, direction: "ltr", textAlign: "right" }} value={f.email} onChange={e => u("email", e.target.value)} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>יום הולדת עברי</label>
          <input style={inputStyle} value={f.hebrewBirthday} onChange={e => u("hebrewBirthday", e.target.value)} placeholder={'למשל: י"ד ניסן'} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>תאריך הצטרפות</label>
          <input style={inputStyle} type="date" value={f.joinDate} onChange={e => u("joinDate", e.target.value)} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>נקודות</label>
          <input style={inputStyle} type="number" min={0} value={f.points} onChange={e => u("points", Number(e.target.value || 0))} />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>סטטוס</label>
          <select style={{ ...inputStyle, cursor: "pointer" }} value={f.status} onChange={e => u("status", e.target.value)}>
            <option value="active">פעיל</option>
            <option value="inactive">לא פעיל</option>
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>סוג התנדבות</label>
          <select style={{ ...inputStyle, cursor: "pointer" }} value={f.engagementType || "fixed"} onChange={e => u("engagementType", e.target.value)}>
            <option value="fixed">קבוע</option>
            <option value="casual">מזדמן</option>
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>רכזים משויכים (עד 2)</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 8 }}>
            <select
              style={{ ...inputStyle, cursor: canManageUsers ? "pointer" : "not-allowed", opacity: canManageUsers ? 1 : 0.8 }}
              value={selectedCoordinatorId}
              onChange={e => setSelectedCoordinatorId(e.target.value)}
              disabled={!canManageUsers}
            >
              <option value="">בחר רכז</option>
              {availableCoordinators.map(c => <option key={c.id} value={c.id}>{c.name || c.email || c.id}</option>)}
            </select>
            <button
              type="button"
              style={btnSecondary}
              disabled={!canManageUsers || !selectedCoordinatorId || (f.coordinatorIds || []).length >= 2}
              onClick={() => {
                if (!selectedCoordinatorId) return;
                setF((p) => (p.coordinatorIds.includes(selectedCoordinatorId) ? p : { ...p, coordinatorIds: [...p.coordinatorIds, selectedCoordinatorId].slice(0, 2) }));
                setSelectedCoordinatorId("");
              }}
            >
              <Ic.Plus /> הוסף
            </button>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(f.coordinatorIds || []).map((id) => {
              const c = availableCoordinators.find((row) => row.id === id) || coordinators.find((row) => row.id === id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setF((p) => ({ ...p, coordinatorIds: p.coordinatorIds.filter((rowId) => rowId !== id) }))}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 8, border: `1.5px solid ${ACCENT}`, cursor: "pointer", fontSize: 13, background: ACCENT_LIGHT, color: ACCENT }}
                >
                  {c?.name || c?.email || id} ✕
                </button>
              );
            })}
            {!(f.coordinatorIds || []).length && <span style={{ fontSize: 12, color: TEXT_MUTED }}>אין שיוך</span>}
          </div>
          {canManageUsers && (f.domains || []).length > 0 && availableCoordinators.length === 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: "#B45309" }}>
              אין רכזים עם שיוך תחומים מתאים לתחום/תת־תחום שנבחר.
            </div>
          )}
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>ראשי צוות (עד 2)</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 8 }}>
            <select
              style={{ ...inputStyle, cursor: isCurrentGroupCoordinator ? "not-allowed" : "pointer", opacity: isCurrentGroupCoordinator ? 0.8 : 1 }}
              value={selectedTeamLeadId}
              onChange={e => setSelectedTeamLeadId(e.target.value)}
              disabled={isCurrentGroupCoordinator}
            >
              <option value="">בחר ראש צוות</option>
              {availableTeamLeads.map(v => <option key={v.id} value={v.id}>{v.name || "ללא שם"}</option>)}
            </select>
            <button
              type="button"
              style={btnSecondary}
              disabled={isCurrentGroupCoordinator || !selectedTeamLeadId || (f.teamLeadVolunteerIds || []).length >= 2}
              onClick={() => {
                if (!selectedTeamLeadId) return;
                setF((p) => (p.teamLeadVolunteerIds.includes(selectedTeamLeadId) ? p : { ...p, teamLeadVolunteerIds: [...p.teamLeadVolunteerIds, selectedTeamLeadId].slice(0, 2) }));
                setSelectedTeamLeadId("");
              }}
            >
              <Ic.Plus /> הוסף
            </button>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(f.teamLeadVolunteerIds || []).map((id) => {
              const lead = availableTeamLeads.find((row) => row.id === id) || volunteers.find((row) => row.id === id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setF((p) => ({ ...p, teamLeadVolunteerIds: p.teamLeadVolunteerIds.filter((rowId) => rowId !== id) }))}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 8, border: `1.5px solid ${ACCENT}`, cursor: "pointer", fontSize: 13, background: ACCENT_LIGHT, color: ACCENT }}
                >
                  {lead?.name || id} ✕
                </button>
              );
            })}
            {!(f.teamLeadVolunteerIds || []).length && <span style={{ fontSize: 12, color: TEXT_MUTED }}>אין שיוך</span>}
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: TEXT_SECONDARY }}>
            <input
              type="checkbox"
              checked={f.isTeamLead === true}
              onChange={e => setF(p => ({ ...p, isTeamLead: e.target.checked }))}
            />
            להגדיר כראש צוות
          </label>
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>תחומי פעילות</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, alignItems: "end", marginBottom: 10 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: TEXT_MUTED, marginBottom: 4 }}>קטגוריה ראשית</label>
            <select
              style={{ ...inputStyle, cursor: "pointer" }}
              value={selectedMainDomainId}
              onChange={e => { setSelectedMainDomainId(e.target.value); setSelectedChildDomainId(""); }}
            >
              {visibleMainDomains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: TEXT_MUTED, marginBottom: 4 }}>תת קטגוריה</label>
            <select
              style={{ ...inputStyle, cursor: "pointer" }}
              value={selectedChildDomainId}
              onChange={e => setSelectedChildDomainId(e.target.value)}
            >
              <option value="">ללא תת קטגוריה</option>
              {visibleChildDomains.map(d => <option key={d.id} value={d.id}>{d.name}{(d.city || "").trim() ? ` (${d.city})` : ""}</option>)}
            </select>
          </div>
          <button type="button" style={btnSecondary} onClick={addSelectedDomainToVolunteer}>
            <Ic.Plus /> הוסף
          </button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {f.domains.map(id => {
            const d = domainById[id];
            const parentName = d?.parentDomainId ? (domainById[d.parentDomainId]?.name || "") : "";
            const cityLabel = (d?.city || "").trim() ? ` (${d.city})` : "";
            const label = d ? (parentName ? `${parentName} / ${d.name}${cityLabel}` : `${d.name}${cityLabel}`) : id;
            return (
              <button
                key={id} type="button"
                onClick={() => removeDomainFromVolunteer(id)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "5px 10px", borderRadius: 8, border: `1.5px solid ${ACCENT}`,
                  cursor: "pointer", fontSize: 13, background: ACCENT_LIGHT, color: ACCENT, userSelect: "none",
                }}
                title="לחץ להסרה"
              >
                {d && <span style={{ width: 7, height: 7, borderRadius: "50%", background: d.color }} />}
                {label} ✕
              </button>
            );
          })}
          {f.domains.length === 0 && <span style={{ fontSize: 12, color: TEXT_MUTED }}>לא נבחרו תחומים</span>}
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>הערות</label>
        <textarea style={{ ...inputStyle, resize: "vertical", minHeight: 80 }} value={f.notes} onChange={e => u("notes", e.target.value)} />
      </div>
    </Modal>
  );
}
